// Mentor browse home (PRD §7.4) — best-fit companies + their open missions,
// upcoming Favcy calls, and a share-my-profile card.
//
// PRODUCT DECISION 2026-05-13 (user-asked): show ALL active companies, not
// only those with open missions. The mentor should never land on "0 best-fit
// companies". Companies without an open mission render with a polite empty
// state per card so the mentor still sees the portfolio.
import { useEffect, useState } from "react";
import { Link } from "wouter";
import { Sparkles, ArrowRight, Calendar, Video, Share2, Copy, Check, ExternalLink } from "lucide-react";
import ProgressLoader from "@/components/progress-loader";

const BASE = import.meta.env.BASE_URL;

interface Mission { id: number; title: string; status: string; domainTags: string[]; authorKind: string; }
interface Company { companyId: number; companyName: string; sector: string | null; stage: string | null; description: string; score: number; tagOverlap: string[]; missions: Mission[]; hasOpenInterest: boolean; }
interface CallRow { id: number; engagementId: number; status: string; kind: string; scheduledStartUtc: string | null; durationMin: number | null; googleMeetUrl: string | null; agenda: string | null; missionTitle: string; companyName: string; }
interface HomeData { mentorId: number; mentorName: string; publicProfileOptIn: boolean; companies: Company[]; activeEngagementCount: number; pendingInterestCount: number; }

export default function MentorHome() {
  const [data, setData] = useState<HomeData | null>(null);
  const [calls, setCalls] = useState<CallRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Share-profile state.
  const [shareBusy, setShareBusy] = useState(false);
  const [shareErr, setShareErr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const tok = localStorage.getItem("mentor_token") || "";
    const headers = { "x-mentor-token": tok };
    fetch(`${BASE}api/mentor-portal/home`, { headers })
      .then(r => r.ok ? r.json() : r.json().then(d => Promise.reject(d.error || `${r.status}`)))
      .then(setData)
      .catch(e => setErr(String(e)));
    // Calls strip — fetched in parallel; non-blocking.
    fetch(`${BASE}api/mentor-portal/intro-calls`, { headers })
      .then(r => r.ok ? r.json() : { calls: [] })
      .then(d => setCalls(d.calls || []))
      .catch(() => setCalls([]));
  }, []);

  async function togglePublic(enabled: boolean): Promise<void> {
    if (!data) return;
    setShareBusy(true); setShareErr(null);
    try {
      const tok = localStorage.getItem("mentor_token") || "";
      const r = await fetch(`${BASE}api/mentor-portal/public-profile-toggle`, {
        method: "POST",
        headers: { "x-mentor-token": tok, "Content-Type": "application/json" },
        body: JSON.stringify({ enabled }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.error || "Could not save.");
      }
      setData({ ...data, publicProfileOptIn: enabled });
    } catch (e: any) {
      setShareErr(e?.message || "Could not save.");
    } finally {
      setShareBusy(false);
    }
  }

  function publicProfileUrl(): string {
    if (!data) return "";
    return `${window.location.origin}/m/${data.mentorId}`;
  }

  async function copyLink(): Promise<void> {
    try {
      await navigator.clipboard.writeText(publicProfileUrl());
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setShareErr("Could not copy. Long-press the link to copy manually.");
    }
  }

  async function nativeShare(): Promise<void> {
    const url = publicProfileUrl();
    const text = `My Favcy Select mentor profile`;
    // navigator.share is only available on HTTPS + supported browsers
    // (mostly mobile). Fall back to copy when unavailable.
    if (typeof (navigator as any).share === "function") {
      try { await (navigator as any).share({ title: text, text, url }); return; } catch { /* user cancelled */ return; }
    }
    void copyLink();
  }

  if (err) return <div className="max-w-3xl mx-auto px-6 py-10 text-sm text-red-400" data-testid="text-mentor-home-error">{err}</div>;
  if (!data) return (
    <div className="max-w-3xl mx-auto px-6 py-10">
      <ProgressLoader active={true} title="Loading" stages={["Loading your profile", "Pulling open missions across cohorts", "Ranking companies for fit"]} />
    </div>
  );

  const upcomingCalls = (calls || []).filter(c => c.status === "scheduled" && c.scheduledStartUtc);
  const pendingSlotCalls = (calls || []).filter(c => c.status === "pending_slot");
  const profileUrl = publicProfileUrl();

  return (
    <div className="max-w-5xl mx-auto px-6 py-8 space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Browse companies</h1>
        <p className="text-sm text-[var(--mentor-fg-muted)] mt-1">
          {data.companies.length === 0
            ? "No companies in the portfolio yet."
            : `Showing all ${data.companies.length} companies, ranked by fit with your expertise.`}
          {data.pendingInterestCount > 0 && ` You have ${data.pendingInterestCount} pending interest${data.pendingInterestCount === 1 ? "" : "s"}.`}
          {data.activeEngagementCount > 0 && ` ${data.activeEngagementCount} active engagement${data.activeEngagementCount === 1 ? "" : "s"}.`}
        </p>
      </div>

      {/* Calls strip — only renders when there's something to show, so the
          empty state stays clean for mentors who haven't booked anything. */}
      {(upcomingCalls.length > 0 || pendingSlotCalls.length > 0) && (
        <section className="bg-[var(--mentor-card)] border border-[var(--mentor-border)] rounded-lg p-5" data-testid="section-mentor-calls">
          <div className="flex items-center gap-2 mb-3">
            <Calendar className="w-4 h-4 text-[var(--mentor-accent)]" />
            <h2 className="text-base font-bold">Your calls</h2>
          </div>
          <div className="space-y-2">
            {pendingSlotCalls.map((c) => (
              <Link key={c.id} href={`/mentor/engagements/${c.engagementId}`} className="flex items-start justify-between gap-3 px-3 py-2.5 rounded-md bg-[var(--mentor-bg)] border border-[var(--mentor-border)] hover:border-[#FF5C00]/40 transition" data-testid={`call-pending-${c.id}`}>
                <div>
                  <div className="text-sm font-medium">Awaiting time slot · {c.companyName}</div>
                  <div className="text-[11px] text-[var(--mentor-fg-muted)] mt-0.5">{c.missionTitle} · pick a time on the engagement page</div>
                </div>
                <ArrowRight className="w-4 h-4 text-[var(--mentor-fg-muted)] mt-0.5" />
              </Link>
            ))}
            {upcomingCalls.map((c) => {
              const start = c.scheduledStartUtc ? new Date(c.scheduledStartUtc) : null;
              const when = start ? start.toLocaleString("en-IN", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "TBD";
              return (
                <div key={c.id} className="flex items-start justify-between gap-3 px-3 py-2.5 rounded-md bg-[var(--mentor-bg)] border border-[var(--mentor-border)]" data-testid={`call-scheduled-${c.id}`}>
                  <div>
                    <div className="text-sm font-medium">{when} · {c.companyName}</div>
                    <div className="text-[11px] text-[var(--mentor-fg-muted)] mt-0.5">{c.missionTitle} · {c.durationMin || 30} min · {c.kind === "intro" ? "Intro call" : "Working call"}</div>
                  </div>
                  {c.googleMeetUrl ? (
                    <a href={c.googleMeetUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[var(--mentor-accent)] hover:underline" data-testid={`call-meet-${c.id}`}>
                      <Video className="w-3.5 h-3.5" /> Join
                    </a>
                  ) : null}
                </div>
              );
            })}
          </div>
          <p className="text-[11px] text-[var(--mentor-fg-muted)] mt-3">
            We will also email you the calendar invite. If you have connected Google Calendar on the activate page, calls land directly on your calendar.
          </p>
        </section>
      )}

      {/* Share-my-profile card */}
      <section className="bg-[var(--mentor-card)] border border-[var(--mentor-border)] rounded-lg p-5" data-testid="section-share-profile">
        <div className="flex items-start justify-between gap-4 mb-2">
          <div className="flex items-center gap-2">
            <Share2 className="w-4 h-4 text-[var(--mentor-accent)]" />
            <h2 className="text-base font-bold">Share your Favcy mentor profile</h2>
          </div>
          <label className="flex items-center gap-2 text-xs text-[var(--mentor-fg-muted)] cursor-pointer select-none">
            <input
              type="checkbox"
              checked={data.publicProfileOptIn}
              onChange={(e) => void togglePublic(e.target.checked)}
              disabled={shareBusy}
              className="accent-[#FF5C00]"
              data-testid="checkbox-public-profile-opt-in"
            />
            {data.publicProfileOptIn ? "Public" : "Private"}
          </label>
        </div>
        <p className="text-xs text-[var(--mentor-fg-muted)] leading-relaxed">
          Turn this on to make your profile shareable. We will publish your name, headline, bio, expertise, and location at a public link. We will never publish your email, your compensation terms, or any private notes.
        </p>
        {shareErr && <div className="text-xs text-red-400 mt-2">{shareErr}</div>}
        {data.publicProfileOptIn && (
          <div className="mt-4 space-y-3">
            <div className="flex items-center gap-2">
              <input
                readOnly
                value={profileUrl}
                onFocus={(e) => e.currentTarget.select()}
                className="flex-1 text-xs px-3 py-2 rounded-md bg-[var(--mentor-bg)] border border-[var(--mentor-border)] text-[var(--mentor-fg)]"
                data-testid="input-public-profile-url"
              />
              <button
                type="button"
                onClick={copyLink}
                className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-md border border-[var(--mentor-border)] hover:border-[#FF5C00]/40 transition"
                data-testid="btn-copy-profile-link"
              >
                {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button type="button" onClick={() => void nativeShare()} className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-md bg-[#FF5C00] text-white hover:bg-[#E55200] transition" data-testid="btn-share-native">
                <Share2 className="w-3.5 h-3.5" /> Share
              </button>
              <a href={`https://wa.me/?text=${encodeURIComponent("My Favcy Select mentor profile: " + profileUrl)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-md border border-[var(--mentor-border)] hover:border-[#FF5C00]/40 transition" data-testid="btn-share-whatsapp">
                WhatsApp
              </a>
              <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(profileUrl)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-md border border-[var(--mentor-border)] hover:border-[#FF5C00]/40 transition" data-testid="btn-share-linkedin">
                LinkedIn
              </a>
              <a href={`mailto:?subject=${encodeURIComponent("My Favcy Select mentor profile")}&body=${encodeURIComponent(profileUrl)}`} className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-md border border-[var(--mentor-border)] hover:border-[#FF5C00]/40 transition" data-testid="btn-share-email">
                Email
              </a>
              <a href={profileUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-md border border-[var(--mentor-border)] hover:border-[#FF5C00]/40 transition ml-auto" data-testid="btn-preview-profile">
                <ExternalLink className="w-3.5 h-3.5" /> Preview
              </a>
            </div>
          </div>
        )}
      </section>

      {/* Browse companies — show ALL, with a per-card empty state when there
          are no open missions. Mentor still sees the company so they know the
          portfolio exists. */}
      {data.companies.length === 0 ? (
        <div className="bg-[var(--mentor-card)] border border-[var(--mentor-border)] rounded-lg p-8 text-center text-sm text-[var(--mentor-fg-muted)]">
          No companies in the portfolio yet. Check back in a few days.
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {data.companies.map((c) => (
            <Link key={c.companyId} href={`/mentor/companies/${c.companyId}`}
              className="bg-[var(--mentor-card)] border border-[var(--mentor-border)] rounded-lg p-5 hover:border-[#FF5C00]/40 transition flex flex-col"
              data-testid={`company-card-${c.companyId}`}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="min-w-0">
                  <h3 className="text-base font-bold truncate" data-testid={`company-name-${c.companyId}`}>{c.companyName}</h3>
                  <div className="text-xs text-[var(--mentor-fg-muted)] mt-0.5">
                    {[c.sector, c.stage].filter(Boolean).join(" · ") || "Stage not set"}
                  </div>
                </div>
                {c.tagOverlap.length > 0 && (
                  <div className="flex items-center gap-1 text-[10px] text-[var(--mentor-accent)] flex-shrink-0">
                    <Sparkles className="w-3 h-3" /> {c.tagOverlap.length}
                  </div>
                )}
              </div>

              <p className="text-sm text-[var(--mentor-fg)] leading-snug line-clamp-2 mb-3">
                {c.description?.trim() || <span className="text-[var(--mentor-fg-muted)] italic">No description on file yet.</span>}
              </p>

              <div className="mt-auto flex items-center justify-between text-xs text-[var(--mentor-fg-muted)]">
                <span>
                  {c.missions.length > 0
                    ? `${c.missions.length} open mission${c.missions.length === 1 ? "" : "s"}`
                    : "No open missions yet"}
                </span>
                <span className="inline-flex items-center gap-1 text-[var(--mentor-fg)]">
                  View company <ArrowRight className="w-3 h-3" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
